/**
 * Explore's first row: the selected metric over time with its total (and,
 * when comparing, a lighter Previous Period line under the Current Period
 * one), beside a donut of that metric by the view's dimension (top 8).
 */

import type { ReactNode } from 'react';

import { LineChart, seriesTone } from '../../charts.tsx';
import { useChangeFormat } from '../../reports/change-format.ts';
import { Donut } from '../../reports/charts-extra.tsx';
import { bucketLabel, formatChange, formatValue } from '../../reports/format.ts';
import type { CatalogueMetric, WidgetResult } from '../../reports/reports-api.ts';

const CHART_HEIGHT = 240;
const PREVIOUS_TONE = 7;

export function ExploreCharts(props: {
  metric: CatalogueMetric;
  cards: WidgetResult | null;
  donut: WidgetResult | null;
  donutLabel: string | null;
}): ReactNode {
  const { metric, cards, donut } = props;
  const changeFormat = useChangeFormat();
  const entry = cards?.metrics.find((item) => item.key === metric.key);
  const change = entry?.change
    ? formatChange(entry.change, metric.higher_is_better !== false, {
        as: changeFormat,
        format: metric.format,
      })
    : null;
  const points = (series: WidgetResult['series'] | null | undefined) =>
    (series ?? []).map((point) => ({
      label: bucketLabel(point.bucket, cards?.granularity ?? 'day'),
      value: point.values[metric.key] ?? 0,
    }));
  const lines = [
    { name: 'Current Period', points: points(cards?.series), tone: 0 },
    ...(cards?.compare_series
      ? [{ name: 'Previous Period', points: points(cards.compare_series), tone: PREVIOUS_TONE }]
      : []),
  ];
  const slices = (donut?.rows ?? []).map((row, index) => ({
    label: row.label || '(not set)',
    value: row.values[metric.key] ?? 0,
    tone: seriesTone(index),
  }));

  return (
    <div className={`ex-row${props.donutLabel ? '' : ' single'}`}>
      <section className="ex-panel" aria-label={`${metric.label} over time`}>
        <div className="ex-panel-head">
          <h2>{metric.label}</h2>
          <span className="ex-total">
            {cards ? formatValue(entry?.value ?? null, metric.format) : '…'}
          </span>
          {change && <span className={`ex-change ${change.tone}`}>{change.text}</span>}
        </div>
        {cards?.state === 'OK' ? (
          <LineChart
            series={lines}
            height={CHART_HEIGHT}
            formatValue={(value) => formatValue(value, metric.format, true)}
            emptyNote={`No ${metric.label} found for your date range`}
          />
        ) : (
          <p className="ex-none">
            {cards ? `No ${metric.label} found for your date range` : 'Loading…'}
          </p>
        )}
        {lines.length > 1 && (
          <ul className="ex-legend">
            {lines.map((line) => (
              <li key={line.name}>
                <span className="ex-swatch" style={{ background: seriesTone(line.tone) }} />
                {line.name}
              </li>
            ))}
          </ul>
        )}
      </section>
      {props.donutLabel && (
        <section className="ex-panel" aria-label={`${metric.label} by ${props.donutLabel}`}>
          <div className="ex-panel-head">
            <h2>
              {metric.label} by {props.donutLabel}
            </h2>
          </div>
          {donut?.state === 'OK' && slices.length > 0 ? (
            <Donut
              slices={slices}
              height={CHART_HEIGHT}
              format={(value) => formatValue(value, metric.format)}
            />
          ) : (
            <p className="ex-none">
              {donut ? `No ${metric.label} found for your date range` : 'Loading…'}
            </p>
          )}
        </section>
      )}
    </div>
  );
}
